import React, { useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ReactContentEditable from 'react-contenteditable'

import './content-editable.scss'

const baseClass= 'wb-content-editable'

/**
 * Render a div with contenteditable contents, and be notified of changes with
 * callback functions.
 *
 * This component wraps the
 * [react-contenteditable](https://github.com/lovasoa/react-contenteditable)
 * with convenience functions to use React hooks which are not supported by
 * the library.
 */
const ContentEditable = ({
  innerRef,
  onChange,
  onBlur,
  onKeyPress,
  onKeyDown,
  placeholder,
  html,
  ...rest
}) => {
  const onBlurRef = useRefCallback(() => {
    onBlur && onBlur(innerRef.current.innerHTML)
  })

  const onChangeRef = useRefCallback(event => {
    const textValue = getInnerTextFromHTMLString(event.target.value)
    onChange && onChange(textValue)
  })

  const onKeyPressRef = useRefCallback(event => {
    cancelDefaultBehaviourOnEnter(event)
    onKeyPress && onKeyPress(event)
  })

  const onKeyDownRef = useRefCallback(event => {
    cancelDefaultBehaviourOnEnter(event)
    onKeyDown && onKeyDown(event)
  })

  const { className, ...passThroughProps } = rest
  const classes = classNames(baseClass, {
    [`${baseClass}--showing-placeholder`]: !html || html.length == 0
  }, className)

  return (
    <ReactContentEditable
      data-placeholder={placeholder}
      className={classes}
      {...passThroughProps}
      html={html}
      innerRef={innerRef}
      onChange={onChangeRef}
      onBlur={onBlurRef}
      onKeyPress={onKeyPressRef}
      onKeyDown={onKeyDownRef} />
  )
}

const isEnterKey = (event) => (event.keyCode === 13 || event.key === 'Enter')
const cancelDefaultBehaviourOnEnter = (event) => {
  if (isEnterKey(event)) {
    event.preventDefault()
    event.stopPropagation()
  }
}

const getInnerTextFromHTMLString = (htmlString) => {
  var span = document.createElement('span')
  span.innerHTML = `<span>${htmlString}</span>`
  return span.textContent || span.innerText
}

/**
 * There is an issue with hooks and react-contenteditable. This wrapper
 * addresses that issue, and adds other functionality such as a placeholder. See
 * https://github.com/lovasoa/react-contenteditable/issues/161#issuecomment-736053428
 */
const useRefCallback = (value, deps) => {
  const ref = useRef(value)

  useEffect(() => {
    ref.current = value
  }, deps !== undefined && deps !== null ? deps : [value])

  const result = useCallback((...args) => {
    return ref.current ? ref.current(...args) : null
  }, [])

  return result
}

ContentEditable.propTypes = {
  /** The element's ref */
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  /** Callback function run when content changes */
  onChange: PropTypes.func,
  /** Callback function run on blue */
  onBlur: PropTypes.func,
  /** Callback function run on key press */
  onKeyPress: PropTypes.func,
  /** Callback function run on key down */
  onKeyDown: PropTypes.func,
  /** Placeholder displayed if the element is empty */
  placeholder: PropTypes.string,
  /** The value of the elements contents (innerHTML) */
  html: PropTypes.string.isRequired
}

export default ContentEditable
